import { Component, Input, OnInit, HostBinding } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { VCard4, parseVCards } from 'vcard4-ts';

@Component({
  selector: 'app-media-preview',
  templateUrl: './media-preview.component.html',
  styleUrls: ['./media-preview.component.scss']
})
export class MediaPreviewComponent implements OnInit {
  @Input() title: string;
  @Input() type: 'IMAGE' | 'VIDEO' | 'VCARD';
  @Input() url: string;

  /**
   * Binds the '.vcard-preview' class to the host element (<app-media-preview>)
   * when the component's media type is 'VCARD'.
   *
   * This approach is used in conjunction with the `:host(.vcard-preview)` selector
   * in the component's stylesheet. It allows us to apply styles directly to the
   * component's host element itself, rather than a child element inside its template.
   *
   * Why this is preferred:
   * 1.  Encapsulation: The component manages its own appearance based on its inputs,
   *     making it a self-contained and reusable unit.
   * 2.  Robust Styling: It styles the component's entire "box," preventing potential
   *     layout issues like transparent gaps that could occur if only a child div
   *     had a background.
   * 3.  Clean Architecture: It maintains a clear separation of concerns, where the
   *     component's logic dictates its state, and the stylesheet reacts to that state
   *     to apply the appropriate look.
   */
  @HostBinding('class.vcard-preview')
  get isVCard(): boolean {
    return this.type === 'VCARD';
  }

  vCard: VCard4;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.type === 'VCARD') {
      this.http
        .get(this.url, { responseType: 'text' })
        .subscribe(vcf => (this.vCard = parseVCards(vcf, true).vCards[0]));
    }
  }

  getVCardPhotoSrc(): string {
    if (this.vCard?.PHOTO?.[0]) {
      const photo = this.vCard.PHOTO[0];
      let mimeType = 'image/jpeg'; // Default MIME type
      if (photo.parameters?.TYPE?.[0]) {
        mimeType = `image/${photo.parameters.TYPE[0].toLowerCase()}`;
      }
      return `data:${mimeType};base64,${photo.value}`;
    }
    return null;
  }

  getOrgName(): string {
    const org = this.vCard?.ORG?.[0]?.value;
    if (Array.isArray(org)) {
      return org[0];
    }
    return org;
  }

  getInitials(): string {
    const fn = this.vCard?.FN?.[0]?.value;
    const n = this.vCard?.N?.value;

    if (!fn || !n) {
      return '';
    }

    const familyName = Array.isArray(n.familyNames) ? n.familyNames[0]?.[0] : n.familyNames;
    return `${fn.charAt(0)}${familyName?.charAt(0) || ''}`;
  }
}
